import cookie from 'js-cookie';
import React from 'react';
import { Fetch } from 'react-request';

import { WAREHOUSE_API_URL } from 'config/endpoints';
import * as warehouse from './api';

/**
 * We are using the react-request npm module which provides a
 * declarative model for specifying API dependencies.
 *
 * The declarative API is modeled on Apollo and uses the
 * "children as function" or "render prop" pattern.
 *
 * This module also provides desirable charactistics like:
 * 1. de-duping of multiple identical requests
 * 2. configurable cache policies
 * 3. configurable fetch polices (cache-first, network-only, ...)
 * 3. hooks for integrating with redux or other state management tools
 * 4. uses fetch under the hood (fetch polyfill already provided with CRA)
 *
 * Example Usage (see External link for full documentation):
 * export default (props) => (
 *   <Fetch url={'...some url...'}>
 *      {response => {
 *         if (response.fetching) { return <LoadingIndicator />; }
 *         if (response.failed) {
 *           return <ErrorPage />;
 *         }
 *         if (response.data) { return (...render component as usual with data now available...) }
 *         return null;
 *      }}
 *   </Fetch>
 * )
 *
 * External:
 * https://github.com/jamesplease/react-request
 */

// =================================================================================
// We explictly do NOT include credentials (cookies) with warehouse API requests.
// This is possible because all of our requests are cross-origin.
// This allows us to use only JWT tokens for auth when making warehouse API requests.
// Should have no effect on the warehouse API admin app which is served same-origin.
// ==================================================================================
const FetchBase = ({ url, ...rest }) => {
  return (
    <Fetch
      url={url}
      credentials='omit' // this is default for fetch but making it explicit
      headers={{
        'x-modsy-client-type': 'wc',
        'x-modsy-client-version': process.env.REACT_APP_VERSION || 'local'
      }}
      {...rest}
    />
  );
};

// ==================================================================
// Routes that require JWT Authorization token.
// We expect that the JWT token is set on a cookie called 'jwtToken'.
// Whenever the user logs in or logs out, this cookie should be reset.
// ===================================================================
const FetchWithJwt = ({ url, ...rest }) => {
  return (
    <FetchBase
      url={url}
      headers={{
        'Authorization': `JWT ${cookie.get('jwtToken')}`,
        'x-modsy-client-type': 'wc',
        'x-modsy-client-version': process.env.REACT_APP_VERSION || 'local'
      }}
      {...rest}
    />
  );
};

// =============================
// Declarative Fetch Components.
// These are our exported APIs.
// =============================

/**
 * Fetches space metadata for the provided space ID.
 * Requires valid JWT token.
 *
 * If userId and token are provided, fetches shared space.
 * In this case, a JWT token is not required.
 *
 * If userId is provided, fetches space with admin privileges.
 * Requires valid admin JWT token.
 */
export function GetSpace ({ spaceId, userId, token, children, ...rest }) {
  if (userId && token) {
    const query = `user_id=${userId}&space_id=${spaceId}&token=${token}`;
    return (
      <FetchBase
        url={`${WAREHOUSE_API_URL}/warehouse/api/spaces/${spaceId}?${query}`}
        requestName='getSharedSpace'
        children={children}
        {...rest}
      />
    );
  }

  return (
    <FetchWithJwt
      url={`${WAREHOUSE_API_URL}/warehouse/api/spaces/${spaceId}`}
      requestName='getSpace'
      children={children}
      {...rest}
    />
  );
}

/**
 * Fetches space metadata for the provided space ID using V2 API version.
 * Requires valid JWT token.
 *
 * If userId and token are provided, fetches shared space.
 * In this case, a JWT token is not required.
 *
 * If userId is provided, fetches space with admin privileges.
 * Requires valid admin JWT token.
 */
export function GetSpaceV2 ({ loggedIn, shared, spaceId, userId, token, children, ...rest }) {
  const loggedInParam = loggedIn ? '&loggedIn=true' : '';
  if (shared) {
    return (
      <FetchBase
        url={`${WAREHOUSE_API_URL}/warehouse/api/v2/spaces/${spaceId}/shared?thumbnail=true${loggedInParam}`}
        requestName='getSharedSpace'
        children={children}
        {...rest}
      />
    );
  }

  return (
    <FetchWithJwt
      url={`${WAREHOUSE_API_URL}/warehouse/api/v2/spaces/${spaceId}?thumbnail=true`}
      requestName='getSpace'
      children={children}
      {...rest}
    />
  );
}

/**
 * Fetches design metadata for the provided design ID using V2 API version.
 * Requires valid JWT token.
 *
 * If userId and token are provided, fetches shared space.
 * In this case, a JWT token is not required.
 *
 * If userId is provided, fetches design with admin privileges.
 * Requires valid admin JWT token.
 */
export function GetDesignV2 ({ shared, designId, children, loggedIn, ...rest }) {
  const loggedInParam = loggedIn ? '&loggedIn=true' : '';

  if (shared) {
    return (
      <FetchBase
        url={`${WAREHOUSE_API_URL}/warehouse/api/v2/designs/${designId}/shared?filter=full${loggedInParam}`}
        requestName='getSharedDesign'
        children={children}
        {...rest}
      />
    );
  }

  return (
    <FetchWithJwt
      url={`${WAREHOUSE_API_URL}/warehouse/api/v2/designs/${designId}?filter=full`}
      requestName='getDesign'
      children={children}
      {...rest}
    />
  );
}

/**
 * Fetches Product metadata for the provided space ID using V2 API version.
 * Requires valid JWT token.
 */
export function GetSpaceProductsV2 ({ shared, spaceId, children, ...rest }) {
  if (shared) {
    return (
      <FetchBase
        url={`${WAREHOUSE_API_URL}/warehouse/api/v2/spaceproducts/${spaceId}/shared`}
        requestName='getSharedSpaceProducts'
        children={children}
        responseType={({ status }) => status === 500 ? 'text' : 'json'}
        {...rest}
      />
    );
  }

  return (
    <FetchWithJwt
      url={`${WAREHOUSE_API_URL}/warehouse/api/v2/spaceproducts/${spaceId}`}
      requestName='getSpaceProducts'
      children={children}
      responseType={({ status }) => status === 500 ? 'text' : 'json'}
      {...rest}
    />
  );
}

/**
 * Fetches color tags from warehouse.
 */
export function GetColorTags ({ children, ...rest }) {
  return (
    <FetchBase
      url={`${WAREHOUSE_API_URL}/warehouse/api/tags?category=color`}
      requestName='getTagsColor'
      children={children}
      {...rest}
    />
  );
}

/**
 * Fetches material tags from warehouse.
 */
export function GetMaterialTags ({ children, ...rest }) {
  return (
    <FetchBase
      url={`${WAREHOUSE_API_URL}/warehouse/api/tags?category=material`}
      requestName='getTagsMaterial'
      children={children}
      {...rest}
    />
  );
}

/**
 * Fetches orders for a user from warehouse.
 */
export function GetOrders ({ children, ...rest }) {
  return (
    <FetchWithJwt
      url={`${WAREHOUSE_API_URL}/api/orders`}
      requestName='getOrders'
      children={children}
      responseType={({ status }) => status === 500 ? 'text' : 'json'}
      {...rest}
    />
  );
}

/**
 * Fetches order details for an order from warehouse.
 */
export function GetOrderDetails ({ children, orderId, ...rest }) {
  return (
    <FetchWithJwt
      url={`${WAREHOUSE_API_URL}/api/orders/${orderId}`}
      requestName='getOrderDetails'
      children={children}
      responseType={({ status }) => status === 500 ? 'text' : 'json'}
      {...rest}
    />
  );
}

export { warehouse as default };
